// We're relying on @react-aria to make appropriate choices re: accessibility here; this is based
// closely on examples from their docs
/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/jsx-props-no-spreading */
import React, {
    HTMLAttributes,
    LabelHTMLAttributes,
    OutputHTMLAttributes,
    SyntheticEvent,
    useState,
    RefObject,
} from 'react';
import { useNumberFormatter } from '@react-aria/i18n';
import { SliderState } from '@react-stately/slider';

import { getSliderNumberFormatConfig } from '@trqk/utils';

import { CurrencyInput, NumberInput, PercentInput } from '../..';
import { Thumb } from '.';
import { defaultInputClassConfig, labelPadding } from '../constants';
import { getStepValue } from '../utils';

interface SliderBodyProps {
    /**
     * Starting value for the slider. Since the SliderBody is build on @react-aria hooks that
     * support multiple thumbs, the value is expressed as an array of numbers; SliderBody currently
     * only supports a single value in this array (i.e. one thumb for the slider).
     */
    defaultValue: number[];
    /**
     * groupProps returned by the useSlider hook from @react-aria/slider
     */
    groupProps: HTMLAttributes<HTMLElement>;
    /**
     * A label for the slider
     */
    label?: string;
    /**
     * Optional CSS classes for the label on the input
     */
    inputLabelClasses?: string;
    /**
     * labelProps returned by the useSlider hook from @react-aria/slider
     */
    labelProps: LabelHTMLAttributes<HTMLLabelElement>;
    /**
     * Optional class config which will override the default border classes on the input
     */
    inputClassConfig?: InputClassConfig;
    /**
     * Width for the <input> element
     */
    inputWidth: number;
    /**
     * Slider's minimum value
     */
    maxValue: number;
    /**
     * Slider's maximum value
     */
    minValue: number;
    /**
     * A number format for both the slider value and min/max labels. See {@link SliderNumberFormat}
     * and numberFormatMap in ../constants.ts
     */
    numberFormat: SliderNumberFormat;
    /**
     * Method to set the state of the value and invoke the onChange passed to either Slider or
     * SliderWithDiscreteSteps
     */
    onChange: (valueList: number[]) => void;
    /**
     * outputProps returned by the useSliderState hook from @react-stately/slider
     */
    outputProps: OutputHTMLAttributes<HTMLOutputElement>;
    /**
     * How to handle an `input` value that doesn't match any of the steps that have been allowed
     * See {@link https://github.com/trqk/trqk-client/blob/develop/packages/components/src/Slider/utils/getStepValue.ts | getStepValue}
     */
    roundType: SliderStepRoundType;
    /**
     * SliderState returned by the useSlider hook from @react-aria/slider
     */
    state: SliderState;
    /**
     * A discrete list of steps that the value is allowed to use
     */
    steps?: number[];
    /**
     * trackProps returned by the useSlider hook from @react-aria/slider
     */
    trackProps: HTMLAttributes<HTMLElement>;
    /**
     * A ref to the track div element
     */
    trackRef: RefObject<HTMLDivElement>;
    /**
     * Overall width; slider track will be smaller than this to allow for width of the thumb when
     * in leftmost or rightmost position
     */
    width: number;
}

/**
 * Renders the body of a slider component. This component was set aside in its own file to allow for
 * SliderWithDiscreteSteps to utilize the same exact rendering logic as Slider. Built from React
 * Aria libraries, based on this example: {@link https://react-spectrum.adobe.com/react-aria/useSlider.html#useslider-hook}
 * @param props - see {@link SliderBodyProps}
 */
export const SliderBody = ({
    defaultValue,
    groupProps,
    label,
    inputLabelClasses = 'text-tertiary-400',
    inputWidth,
    labelProps,
    inputClassConfig = defaultInputClassConfig,
    maxValue,
    minValue,
    numberFormat,
    onChange,
    outputProps,
    roundType,
    state,
    steps,
    trackProps,
    trackRef,
    width,
}: SliderBodyProps): JSX.Element => {
    const [inputHasFocus, setInputHasFocus] = useState(false);
    const [inputValue, setInputValue] = useState(defaultValue?.[0] ?? minValue);
    const { minMax: minMaxFormatOptions } =
        getSliderNumberFormatConfig(numberFormat);
    const minMaxNumberFormatter = useNumberFormatter(minMaxFormatOptions);
    const minLabel = minMaxNumberFormatter.format(minValue);
    const maxLabel = minMaxNumberFormatter.format(maxValue);
    const sliderWidth = width - labelPadding;

    const onInputFocus = (): void => {
        setInputHasFocus(true);
    };

    const onInputChange = (nextValue: number): void => {
        // For each input change, go ahead and set the thumb value to the input regardless of
        // whether props.steps is provided
        setInputValue(nextValue);
    };

    const onInputBlur = (e: SyntheticEvent, asFraction = false): void => {
        const target = e.target as HTMLInputElement;
        let nextValue = parseFloat(target.value.replace(',', ''));

        if (!Number.isNaN(nextValue)) {
            if (asFraction) {
                // eslint-disable-next-line operator-assignment -- operator assignment looks weird with division
                nextValue = nextValue / 100;
            }

            // When props.steps is provided, coerce the thumb value to the nearest step when the
            // input loses focus
            if (steps) {
                const stepValue = getStepValue(nextValue, steps, roundType);

                onChange([stepValue]);
                setInputValue(stepValue);
            } else {
                // We added an on KeyPress listener to support using 'Enter' key to set the value,
                // so we need to enforce the max / min here
                if (nextValue > maxValue) {
                    nextValue = maxValue;
                }

                if (nextValue < minValue) {
                    nextValue = minValue;
                }

                onChange([nextValue]);
                setInputValue(nextValue);
            }
        }

        setInputHasFocus(false);
    };

    return (
        <div
            {...groupProps}
            style={{
                touchAction: 'none',
                width,
            }}
            className="flex flex-col relative items-center"
        >
            {/* Create a flex container for the label and output element. */}
            <div className="flex self-stretch items-center rounded-md bg-white">
                {label && (
                    <label className={inputLabelClasses} {...labelProps}>
                        {label}
                    </label>
                )}
                <output
                    {...outputProps}
                    style={{ flex: '1 0 auto', textAlign: 'end' }}
                >
                    {numberFormat === 'none' && (
                        <NumberInput
                            classConfig={inputClassConfig}
                            decimalPlaces={0}
                            max={maxValue}
                            min={minValue}
                            onFocus={onInputFocus}
                            onChange={onInputChange}
                            onBlur={onInputBlur}
                            value={
                                inputHasFocus
                                    ? inputValue
                                    : state.getThumbValue(0)
                            }
                            width={inputWidth}
                        />
                    )}
                    {numberFormat === 'percent' && (
                        <PercentInput
                            asFraction={true}
                            classConfig={inputClassConfig}
                            decimalPlaces={0}
                            maxPercent={maxValue * 100}
                            minPercent={minValue * 100}
                            onFocus={onInputFocus}
                            onChange={onInputChange}
                            onBlur={(e: SyntheticEvent): void => {
                                onInputBlur(e, true);
                            }}
                            value={
                                inputHasFocus
                                    ? inputValue
                                    : state.getThumbValue(0)
                            }
                            width={inputWidth}
                        />
                    )}
                    {numberFormat === 'USD' && (
                        <CurrencyInput
                            classConfig={inputClassConfig}
                            decimalPlaces={0}
                            defaultValue={state.getThumbValue(0)}
                            max={maxValue}
                            min={minValue}
                            onFocus={onInputFocus}
                            onChange={onInputChange}
                            onBlur={onInputBlur}
                            value={
                                inputHasFocus
                                    ? inputValue
                                    : state.getThumbValue(0)
                            }
                            width={inputWidth}
                        />
                    )}
                </output>
            </div>
            <div
                className="flex justify-between w-full text-sm mt-2"
                style={{ width: sliderWidth }}
            >
                <div>{minLabel}</div>
                <div>{maxLabel}</div>
            </div>
            {/* The track element holds the visible track line and the thumb. */}
            <div
                {...trackProps}
                ref={trackRef}
                style={{
                    height: 30,
                    width: sliderWidth,
                }}
                className="relative w-full"
            >
                <div
                    style={{
                        height: 9,
                        top: 13,
                    }}
                    className="absolute bg-tertiary-200 w-full rounded-full"
                />
                <Thumb index={0} state={state} trackRef={trackRef} />
            </div>
        </div>
    );
};
